import type { ComponentNode } from "../layout/types.js";
import { applyPatchToQrCodeNode } from "./applyRuntimePatches.js";
import { collectBehaviorDependencies } from "./compileEvents.js";
import { compileBehavior } from "./compileBehavior.js";
import { evaluateWithRenderCache } from "./renderCache.js";
import {
  applyRuntimeDomTargetFlags,
  runCompiledNodeBehavior,
  withCurrentNodeGeometry,
} from "./applyNodeBehavior.js";
import type {
  CompiledQrCodeNode,
  CompileNodeContext,
  RenderQrCodeNode,
  RuntimeContext,
} from "./types.js";
import { compileNodeStyle, transitionsTouchTopLeft } from "./helpers.js";
import { uniqueDependencies } from "./method.js";

type QrCodeProps = {
  value?: unknown;
};

function normalizeQrCodeValue(value: unknown): string {
  if (value == null) {
    return "";
  }
  return String(value);
}

export function compileQrCodeNode(
  node: ComponentNode,
  compileContext: CompileNodeContext,
): CompiledQrCodeNode {
  if (node.kind !== "component" || node.component !== "qrCode") {
    throw new Error("Node is not a qrCode component");
  }

  const baseStyle = compileNodeStyle(node.style, node.transitions);
  const props = (node.props ?? {}) as QrCodeProps;
  const behavior = node.behavior
    ? compileBehavior(
        node.behavior,
        node.id,
        compileContext.catalogDeps,
        undefined,
        compileContext.layoutNodeById,
      )
    : undefined;

  const dependencies = uniqueDependencies(
    behavior ? collectBehaviorDependencies(behavior, compileContext) : [],
  );

  return {
    id: node.id,
    dependencies,

    evaluate(ctx: RuntimeContext): RenderQrCodeNode | null {
      return evaluateWithRenderCache(ctx, node.id, dependencies, () => {
        let renderNode: RenderQrCodeNode = {
          id: node.id,
          key: ctx.__nodeInstanceKeyPrefix
            ? `${ctx.__nodeInstanceKeyPrefix}${node.id}`
            : node.id,
          x: node.x,
          y: node.y,
          width: node.width,
          height: node.height,
          zIndex: node.zIndex,
          rotation: node.rotation ?? 0,
          kind: "qrCode",
          value: normalizeQrCodeValue(props.value),
          style: { ...baseStyle },
          hasTopLeftTransition: transitionsTouchTopLeft(node.transitions),
        };

        if (behavior) {
          const behaviorCtx = withCurrentNodeGeometry(ctx, {
            x: node.x,
            y: node.y,
            width: node.width,
            height: node.height,
            rotation: node.rotation ?? 0,
          });
          const { patches, effects, continuations, runtimeDomTarget } =
            runCompiledNodeBehavior(behavior, behaviorCtx, node.id);

          for (const patch of patches) {
            renderNode = applyPatchToQrCodeNode(renderNode, patch);
          }

          ctx.__runtimeEffects?.push(...effects);
          ctx.__runtimeContinuations?.push(...continuations);
          renderNode = applyRuntimeDomTargetFlags(
            renderNode,
            runtimeDomTarget,
            behavior,
          );
        }

        if (renderNode.style.visibility === false) {
          return null;
        }

        return renderNode;
      });
    },
  };
}
